import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from "react";
import PropTypes from "prop-types";

import styles from "./style.css";

function hasError(errors) {
  return errors && errors.length > 0;
}

export function BaseComponent(props, ref) {
  const {
    children,
    show = false,
    row,
    className = "",
    rowIndex,
    previousRow,
    column,
    value,
    pos,
    errors,
    onEditing,
    onCompleted,
    onUnload,
    onStartEditing,
    doubleClickToEdit = false,
    onChange,
    onNext,
    onNextLine,
    onPrevious
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef(null);
  const currentValue = useRef(null);
  useEffect(
    function onShowChange() {
      if (show) {
        containerRef.current.focus();
      } else {
        onUnload && onUnload();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [show, pos]
  );
  useImperativeHandle(ref, () => ({
    dataChanged: (value) => {
      currentValue.current = value;
      onChange && onChange({ value, column, row, rowIndex });
    },
    startEditing: () => {
      setIsEditing(true);
      onEditing && onEditing();
      onStartEditing &&
        onStartEditing({ value, column, row, rowIndex, previousRow });
    },
    dataCompleted: (stopEditing = true) => {
      if (stopEditing) setIsEditing(false);
      onCompleted && onCompleted();
    },
    stopEditing: () => {
      setIsEditing(false);
    },
    moveNext: () => {
      onNext && onNext();
    },
    moveNextLine: () => {
      onNextLine && onNextLine();
    },
    movePrevious: () => {
      onPrevious && onPrevious();
    }
  }));
  return (
    <div
      className={`${styles.editor} ${hasError(errors) ? styles.error : ""} ${
        show ? "editing" : ""
      }`}
      tabIndex={0}
      ref={containerRef}
      style={{
        display: show ? "flex" : "none",
        insetInlineStart: pos.left,
        width: `${column.width}px`
      }}
      onKeyDown={(e) => {
        if (isEditing && e.key !== "Tab") {
          e.stopPropagation();
        }
      }}
      onKeyPress={(e) => {
        if (!column.readOnly) {
          if (e.key === "Enter" && !isEditing && !column.readOnly) {
            setIsEditing(true);
            onEditing && onEditing();
            onStartEditing &&
              onStartEditing({ value, column, row, rowIndex, previousRow });
          } else if (!isEditing) {
            setIsEditing(true);
            onEditing && onEditing(e.key);
            onStartEditing &&
              onStartEditing({ value, column, row, rowIndex, previousRow });
          }
          if (isEditing) e.stopPropagation();
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div
        className={`${styles.content} ${className}`}
        style={{
          pointerEvents: doubleClickToEdit && !isEditing ? "none" : "auto",
          backgroundColor: column.readOnly ? "var(--gray-100)" : ""
        }}
      >
        {show && children}
      </div>
      {hasError(errors) && (
        <ul className={styles.errorList}>
          {errors.map((error, i) => {
            return (
              <li className="tiny" key={`error_${i}`}>
                {error}
              </li>
            );
          })}
        </ul>
      )}
      {doubleClickToEdit && (
        <div
          className={styles.blocker}
          style={{ display: isEditing ? "none" : "" }}
          onDoubleClick={(e) => {
            if (!column.readOnly) {
              setIsEditing(true);
              onEditing && onEditing();
              onStartEditing &&
                onStartEditing({ value, column, row, rowIndex });
            }
          }}
        />
      )}
    </div>
  );
}

BaseComponent.propTypes = {
  children: PropTypes.node,
  column: PropTypes.any,
  row: PropTypes.any,
  show: PropTypes.bool,
  pos: PropTypes.any,
  errors: PropTypes.array,
  onEditing: PropTypes.func,
  onChange: PropTypes.func,
  onCompleted: PropTypes.func,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  className: PropTypes.string,
  rowIndex: PropTypes.number,
  previousRow: PropTypes.any,
  value: PropTypes.any,
  onUnload: PropTypes.func,
  onStartEditing: PropTypes.func,
  onNextLine: PropTypes.func,
  doubleClickToEdit: PropTypes.bool
};

export const Base = forwardRef(BaseComponent);
